import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, KeyRound, Shield, Eye, EyeOff, Check, Lock, Unlock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const programs = [
  { id: 'back-reset', title: 'The Back Reset Program' },
  { id: 'fascia-reset', title: '28 Day Stress Reset' },
  { id: 'slow-wellness', title: 'SLOW WELLNESS' },
  { id: 'crap-cleanse-intro', title: 'C.R.A.P. Cleanse (Intro)' },
  { id: 'crap-cleanse', title: 'C.R.A.P. Cleanse (Full)' },
  { id: 'feel-good-detox-intro', title: 'Feel Good Detox (Intro)' },
  { id: 'feel-good-detox', title: 'Feel Good Detox (Full)' },
  { id: 'vip', title: 'V.I.P. Video Library' },
];

const AccountSettings = ({ isOpen, onClose }) => {
  const { user, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [name, setName] = useState(user?.name || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [saving, setSaving] = useState(false);

  const showMsg = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  const handleUpdateProfile = async () => {
    if (!name.trim()) { showMsg('error', 'Name cannot be empty'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/update-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, name: name.trim() }),
      });
      if (res.ok) {
        await refreshUser(user.id);
        showMsg('success', 'Profile updated!');
      } else {
        const data = await res.json();
        showMsg('error', data.detail || 'Failed to update');
      }
    } catch { showMsg('error', 'Connection error'); }
    setSaving(false);
  };

  const handleChangePassword = async () => {
    if (!currentPassword) { showMsg('error', 'Enter your current password'); return; }
    if (newPassword.length < 6) { showMsg('error', 'New password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { showMsg('error', 'Passwords do not match'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, current_password: currentPassword, new_password: newPassword }),
      });
      if (res.ok) {
        showMsg('success', 'Password changed successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        const data = await res.json();
        showMsg('error', data.detail || 'Failed to change password');
      }
    } catch { showMsg('error', 'Connection error'); }
    setSaving(false);
  };

  if (!isOpen || !user) return null;

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'password', label: 'Password', icon: KeyRound },
    { id: 'programs', label: 'My Programs', icon: Shield },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-x-4 top-[10%] md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-lg bg-white rounded-2xl shadow-2xl z-[61] overflow-hidden max-h-[80vh] flex flex-col"
            data-testid="account-settings-modal"
          >
            {/* Header */}
            <div className="bg-forest p-5 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="font-heading text-xl text-cream">Account Settings</h2>
                <p className="text-cream/60 font-body text-sm">{user.email}</p>
              </div>
              <button onClick={onClose} className="text-cream/70 hover:text-cream transition-colors" data-testid="close-account-settings">
                <X size={20} />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-stone-200 flex-shrink-0">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 font-body text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'text-terracotta border-b-2 border-terracotta bg-terracotta/5'
                      : 'text-stone-500 hover:text-forest'
                  }`}
                  data-testid={`account-tab-${tab.id}`}
                >
                  <tab.icon size={16} />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Message */}
            {message.text && (
              <div className={`mx-5 mt-4 p-3 rounded-lg font-body text-sm ${
                message.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'
              }`}>
                {message.text}
              </div>
            )}

            {/* Content */}
            <div className="p-5 overflow-y-auto flex-1">
              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div className="space-y-4">
                  <div>
                    <label className="block font-body text-sm text-stone-500 mb-1">Name</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full px-4 py-3 border border-stone-200 rounded-xl font-body focus:outline-none focus:ring-2 focus:ring-terracotta/30 focus:border-terracotta"
                      data-testid="profile-name-input"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-sm text-stone-500 mb-1">Email</label>
                    <input
                      type="email"
                      value={user.email}
                      disabled
                      className="w-full px-4 py-3 border border-stone-100 rounded-xl font-body bg-stone-50 text-stone-400"
                    />
                    <p className="text-xs text-stone-400 font-body mt-1">Email cannot be changed</p>
                  </div>
                  <button
                    onClick={handleUpdateProfile}
                    disabled={saving}
                    className="w-full bg-terracotta text-cream py-3 rounded-full font-body font-medium hover:bg-terracotta-dark transition-colors disabled:opacity-50"
                    data-testid="save-profile-btn"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}

              {/* Password Tab */}
              {activeTab === 'password' && (
                <div className="space-y-4">
                  <div className="relative">
                    <label className="block font-body text-sm text-stone-500 mb-1">Current Password</label>
                    <input
                      type={showCurrentPw ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="w-full px-4 py-3 pr-12 border border-stone-200 rounded-xl font-body focus:outline-none focus:ring-2 focus:ring-terracotta/30 focus:border-terracotta"
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPw(!showCurrentPw)}
                      className="absolute right-3 top-8 text-stone-400 hover:text-stone-600"
                    >
                      {showCurrentPw ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <div className="relative">
                    <label className="block font-body text-sm text-stone-500 mb-1">New Password</label>
                    <input
                      type={showNewPw ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Min 6 characters"
                      className="w-full px-4 py-3 pr-12 border border-stone-200 rounded-xl font-body focus:outline-none focus:ring-2 focus:ring-terracotta/30 focus:border-terracotta"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPw(!showNewPw)}
                      className="absolute right-3 top-8 text-stone-400 hover:text-stone-600"
                    >
                      {showNewPw ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <div>
                    <label className="block font-body text-sm text-stone-500 mb-1">Confirm New Password</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-4 py-3 border border-stone-200 rounded-xl font-body focus:outline-none focus:ring-2 focus:ring-terracotta/30 focus:border-terracotta"
                      data-testid="confirm-password-input"
                    />
                    {confirmPassword && newPassword && (
                      <p className={`text-xs font-body mt-1 ${newPassword === confirmPassword ? 'text-emerald-600' : 'text-red-500'}`}>
                        {newPassword === confirmPassword ? 'Passwords match' : 'Passwords do not match'}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={handleChangePassword}
                    disabled={saving}
                    className="w-full bg-terracotta text-cream py-3 rounded-full font-body font-medium hover:bg-terracotta-dark transition-colors disabled:opacity-50"
                    data-testid="change-password-btn"
                  >
                    {saving ? 'Changing...' : 'Change Password'}
                  </button>
                </div>
              )}

              {/* Programs Tab */}
              {activeTab === 'programs' && (
                <div className="space-y-3">
                  <p className="text-stone-500 font-body text-sm mb-4">Programs you currently have access to:</p>
                  {programs.map(program => {
                    const hasAccess = user.unlocked_programs?.includes(program.id);
                    return (
                      <div
                        key={program.id}
                        className={`flex items-center gap-3 p-3 rounded-xl ${
                          hasAccess ? 'bg-emerald-50 border border-emerald-200' : 'bg-stone-50 border border-stone-100'
                        }`}
                      >
                        {hasAccess ? (
                          <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <Unlock size={14} className="text-emerald-600" />
                          </div>
                        ) : (
                          <div className="w-8 h-8 bg-stone-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <Lock size={14} className="text-stone-400" />
                          </div>
                        )}
                        <div className="flex-1">
                          <p className={`font-body text-sm ${hasAccess ? 'text-forest font-medium' : 'text-stone-400'}`}>
                            {program.title}
                          </p>
                        </div>
                        {hasAccess && (
                          <span className="text-xs font-body text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">Active</span>
                        )}
                      </div>
                    );
                  })}
                  <p className="text-stone-400 font-body text-xs text-center mt-4 italic">
                    Contact Jill to add program access
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AccountSettings;
